import isNil from 'lodash/isNil';

import { isNativeContainer } from './index';

/**
 * The push token for the handset the platform is running on, handed in by the
 * native container.
 *
 * The container owns the token because only it can ask the OS for one, and the
 * platform owns the session because only it talks to Auth0. Registration needs
 * both, so the token comes this way and the platform makes the call (Mitch,
 * 2026-09-16).
 */
export type NativeDeviceToken = {
  /** Opaque. An APNs token on iOS, an FCM registration token on Android. */
  deviceToken: string;
  platform: 'ios' | 'android';
  /** Which branded app this build is. Delivery routes on it. */
  app: 'farmbot' | 'ranchbot';
};

/**
 * Both halves of the contract with `nativeBridge.ts` in the app. The container
 * sets the global and then dispatches the event, so a listener registered
 * before the injection hears it and one registered after still finds the
 * global.
 */
const NATIVE_DEVICE_GLOBAL = '__farmbotNativeDevice';
export const NATIVE_DEVICE_EVENT = 'farmbot:native-device';

declare global {
  interface Window {
    __farmbotNativeDevice?: unknown;
  }
}

const PLATFORMS = ['ios', 'android'];
const APPS = ['farmbot', 'ranchbot'];

/**
 * Validated rather than cast, in the same spirit as the container's own
 * `parsePlatformMessage`.
 *
 * Nothing hostile is expected here, since the value is written by the app we
 * ship. The reason to check is an older container: the platform deploys
 * continuously while container updates wait on app review, so a build predating
 * a field will be running against this code. An absent or malformed value must
 * read as "no token yet" and leave the platform working exactly as a browser,
 * not register something the service will reject.
 */
function parseNativeDeviceToken(value: unknown): NativeDeviceToken | null {
  if (isNil(value) || typeof value !== 'object') return null;

  const { deviceToken, platform, app } = value as Record<string, unknown>;

  if (typeof deviceToken !== 'string' || deviceToken === '') return null;
  if (typeof platform !== 'string' || !PLATFORMS.includes(platform)) return null;
  if (typeof app !== 'string' || !APPS.includes(app)) return null;

  return {
    deviceToken,
    platform: platform as NativeDeviceToken['platform'],
    app: app as NativeDeviceToken['app']
  };
}

/** The token the container has already injected, or null outside the app. */
export function getNativeDeviceToken(): NativeDeviceToken | null {
  if (typeof window === 'undefined' || !isNativeContainer()) return null;
  return parseNativeDeviceToken(window[NATIVE_DEVICE_GLOBAL]);
}

/**
 * Subscribe to the token arriving, and returns an unsubscribe.
 *
 * Needed as well as the getter because the injection races the page. The
 * container injects on every load, but a load it considers finished is not the
 * same moment React mounts, so the token may land either side of it.
 *
 * The listener can fire repeatedly with the same token. The container
 * deliberately re-injects on every load, because a reload takes the global with
 * it, so the caller dedupes rather than this.
 */
export function subscribeToNativeDeviceToken(
  listener: (device: NativeDeviceToken) => void
): () => void {
  if (typeof window === 'undefined' || !isNativeContainer()) return () => undefined;

  const handler = (event: Event) => {
    const device = parseNativeDeviceToken((event as CustomEvent).detail);
    if (device) listener(device);
  };

  window.addEventListener(NATIVE_DEVICE_EVENT, handler);
  return () => window.removeEventListener(NATIVE_DEVICE_EVENT, handler);
}
